import React, { useState, useEffect } from 'react';
import { IonIcon, IonFab, IonFabButton, IonSkeletonText } from '@ionic/react';
import {
  add, trash, copy, settings, pencil, apps, checkmark, close,
  gridOutline, alertCircleOutline, colorPaletteOutline,
} from 'ionicons/icons';
import {
  getDashboards, createDashboard, deleteDashboard, duplicateDashboard, updateDashboard,
  getTemplates, createDashboardFromTemplate, getWidgetDefinitions, addWidget,
  type Dashboard, type DashboardTemplate, type WidgetDefinition, type WidgetCategory,
} from '../services/dashboard';

// UI Library imports
import { PageShell } from '../components/ui/PageShell';
import { Button } from '../components/ui/Button';
import { Modal, ModalSection } from '../components/ui/Modal';
import { Search } from '../components/ui/Search';
import { Segment, type SegmentOption } from '../components/ui/Segment';
import { Badge } from '../components/ui/Badge';
import { TextInput } from '../components/ui/TextInput';
import { Card } from '../components/ui/Card';
import { List, type ListItemProps } from '../components/ui/List';

// Layout Primitives
import { Container, Stack, AutoGrid } from '../components/ui/Layout';

// Dashboard Settings Modal
import DashboardSettingsModal from '../components/DashboardSettingsModal';

import './DashboardEditor.css';

interface DashboardEditorProps {
  householdId: string;
  onSelectDashboard?: (dashboardId: string, editMode?: boolean) => void;
}

// Widget category options for Segment
const WIDGET_CATEGORY_OPTIONS: SegmentOption<WidgetCategory | 'all'>[] = [
  { value: 'all', label: 'All' },
  { value: 'time', label: '⏰ Time' },
  { value: 'calendar', label: '📅 Calendar' },
  { value: 'tasks', label: '✅ Tasks' },
  { value: 'family', label: '👨‍👩‍👧‍👦 Family' },
  { value: 'media', label: '🖼️ Media' },
  { value: 'info', label: 'ℹ️ Info' },
  { value: 'travel', label: '🚗 Travel' },
];

const DashboardEditor: React.FC<DashboardEditorProps> = ({ householdId, onSelectDashboard }) => {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [templates, setTemplates] = useState<DashboardTemplate[]>([]);
  const [widgetDefs, setWidgetDefs] = useState<WidgetDefinition[]>([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showNewModal, setShowNewModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Form state
  const [newName, setNewName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);

  // Widget picker state
  const [widgetCategory, setWidgetCategory] = useState<WidgetCategory | 'all'>('all');
  const [widgetSearch, setWidgetSearch] = useState('');

  // Action target
  const [actionDashboard, setActionDashboard] = useState<Dashboard | null>(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const [dashData, templateData, widgetData] = await Promise.all([
        getDashboards(householdId),
        getTemplates(),
        getWidgetDefinitions(),
      ]);
      setDashboards(dashData);
      setTemplates(templateData);
      setWidgetDefs(widgetData);
      setLoading(false);
    };
    loadData();
  }, [householdId]);

  // Create new dashboard
  const handleCreateDashboard = async () => {
    if (!newName.trim()) return;

    let dashboardId: string | null = null;

    if (selectedTemplate) {
      dashboardId = await createDashboardFromTemplate(householdId, selectedTemplate, newName);
    } else {
      const dashboard = await createDashboard(householdId, { name: newName });
      dashboardId = dashboard?.id || null;
    }

    if (dashboardId) {
      const updated = await getDashboards(householdId);
      setDashboards(updated);
      setShowNewModal(false);
      setShowTemplateModal(false);
      setNewName('');
      setSelectedTemplate(null);
      onSelectDashboard?.(dashboardId, true);
    }
  };

  // Dashboard actions
  const handleDuplicate = async (dashboard: Dashboard) => {
    const newId = await duplicateDashboard(dashboard.id, `${dashboard.name} (Copy)`);
    if (newId) {
      const updated = await getDashboards(householdId);
      setDashboards(updated);
    }
    setShowActionsModal(false);
  };

  const handleDelete = async () => {
    if (!actionDashboard) return;
    await deleteDashboard(actionDashboard.id);
    setDashboards(prev => prev.filter(d => d.id !== actionDashboard.id));
    setShowDeleteModal(false);
    setActionDashboard(null);
  };

  const handleSetDefault = async (dashboard: Dashboard) => {
    await updateDashboard(dashboard.id, { is_default: true });
    setDashboards(prev => prev.map(d => ({
      ...d,
      is_default: d.id === dashboard.id,
    })));
    setShowActionsModal(false);
  };

  // Add widget to dashboard
  const handleAddWidget = async (widgetType: string) => {
    if (!selectedDashboardId) return;

    const def = widgetDefs.find(w => w.id === widgetType);
    await addWidget(selectedDashboardId, {
      widget_type: widgetType,
      grid_w: def?.default_width || 2,
      grid_h: def?.default_height || 2,
    });

    setShowWidgetPicker(false);
    onSelectDashboard?.(selectedDashboardId, true);
  };

  // Filter widgets
  const filteredWidgets = widgetDefs.filter(w => {
    if (widgetCategory !== 'all' && w.category !== widgetCategory) return false;
    if (widgetSearch && !w.name.toLowerCase().includes(widgetSearch.toLowerCase())) return false;
    return true;
  });

  // Convert dashboards to List items
  const dashboardItems: ListItemProps[] = dashboards.map(dashboard => ({
    id: dashboard.id,
    label: dashboard.name,
    description: dashboard.description || 'No description',
    start: {
      element: <span style={{ fontSize: 24 }}>{dashboard.icon || '📊'}</span>,
    },
    end: {
      element: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {dashboard.is_default && (
            <Badge variant="primary" size="sm">Default</Badge>
          )}
          <Button
            fill="clear"
            size="small"
            iconStart={settings}
            onClick={(e) => {
              e.stopPropagation();
              setActionDashboard(dashboard);
              setShowActionsModal(true);
            }}
            aria-label="Dashboard settings"
          />
        </div>
      ),
    },
    onClick: () => onSelectDashboard?.(dashboard.id),
  }));

  // FAB click handler with debug
  const handleFabClick = () => {
    console.log('[DashboardEditor] FAB clicked - opening template modal');
    setShowTemplateModal(true);
  };

  return (
    <PageShell
      title="Dashboards"
      headerType="page"
      showSettings
      noPadding
    >
      <Container size="lg" padding="md" className="dashboard-content">
        <Stack gap="lg">
          {/* Loading State */}
          {loading ? (
            <Stack gap="md">
              <IonSkeletonText animated style={{ width: '100%', height: 80, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 80, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 200, borderRadius: 12 }} />
            </Stack>
          ) : (
            <>
              {/* Dashboard List */}
              {dashboards.length > 0 ? (
                <List
                  items={dashboardItems}
                  variant="default"
                  size="md"
                  lines="inset"
                />
              ) : (
                <Card style={{ textAlign: 'center' }}>
                  <Stack gap="md" align="center" style={{ padding: '32px 16px' }}>
                    <IonIcon
                      icon={gridOutline}
                      style={{ fontSize: 48, color: 'var(--ion-color-medium)' }}
                    />
                    <p style={{
                      fontFamily: 'var(--kff-data-font)',
                      color: 'var(--ion-color-medium)',
                      margin: 0,
                    }}>
                      No dashboards yet
                    </p>
                    <Button variant="primary" onClick={() => setShowTemplateModal(true)}>
                      Create your first dashboard
                    </Button>
                  </Stack>
                </Card>
              )}

              {/* Templates Section */}
              <Card title="Quick Start Templates">
                {templates.filter(t => t.is_system).length > 0 ? (
                  <AutoGrid minColumnWidth="140px" gap="sm">
                    {templates.filter(t => t.is_system).map(template => (
                      <div
                        key={template.id}
                        className="template-card"
                        onClick={() => {
                          console.log('[DashboardEditor] Template card clicked:', template.name);
                          setSelectedTemplate(template.id);
                          setNewName(template.name);
                          setShowNewModal(true);
                        }}
                      >
                        <span className="template-icon">{template.icon}</span>
                        <span className="template-name">{template.name}</span>
                        <span className="template-desc">{template.description}</span>
                      </div>
                    ))}
                  </AutoGrid>
                ) : (
                  <p style={{
                    fontFamily: 'var(--kff-data-font)',
                    color: 'var(--ion-color-medium)',
                    textAlign: 'center',
                    padding: 16,
                    margin: 0,
                  }}>
                    No templates available
                  </p>
                )}
              </Card>
            </>
          )}
        </Stack>
      </Container>

      {/* FAB for quick add - position adjusted for bottom tabs */}
      <IonFab vertical="bottom" horizontal="end" slot="fixed" style={{ marginBottom: 16 }}>
        <IonFabButton onClick={handleFabClick}>
          <IonIcon icon={add} />
        </IonFabButton>
      </IonFab>

      {/* New Dashboard Modal */}
      <Modal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        size="md"
        header={{ title: 'New Dashboard' }}
        footer={{
          primaryAction: {
            label: 'Create Dashboard',
            onClick: handleCreateDashboard,
            disabled: !newName.trim(),
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => setShowNewModal(false),
          },
        }}
      >
        <ModalSection spacing="normal">
          <TextInput
            label="Dashboard Name"
            value={newName}
            onChange={(val) => setNewName(val)}
            placeholder="Kitchen Display"
          />

          {selectedTemplate && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 0',
            }}>
              <span style={{
                color: 'var(--ion-color-medium)',
                fontFamily: 'var(--kff-data-font)',
                fontSize: 14,
              }}>
                Using template:
              </span>
              <Badge
                variant="primary"
                size="md"
                onDismiss={() => setSelectedTemplate(null)}
              >
                {templates.find(t => t.id === selectedTemplate)?.name}
              </Badge>
            </div>
          )}
        </ModalSection>
      </Modal>

      {/* Template Selection Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        size="lg"
        header={{ title: 'Choose a Template' }}
      >
        <div className="template-list">
          {/* Blank option */}
          <div
            className="template-option"
            onClick={() => {
              setSelectedTemplate(null);
              setNewName('New Dashboard');
              setShowTemplateModal(false);
              setShowNewModal(true);
            }}
          >
            <span className="template-icon">➕</span>
            <div className="template-info">
              <h3>Blank Dashboard</h3>
              <p>Start from scratch</p>
            </div>
          </div>

          {templates.map(template => (
            <div
              key={template.id}
              className="template-option"
              onClick={() => {
                setSelectedTemplate(template.id);
                setNewName(template.name);
                setShowTemplateModal(false);
                setShowNewModal(true);
              }}
            >
              <span className="template-icon">{template.icon}</span>
              <div className="template-info">
                <h3>{template.name}</h3>
                <p>{template.description}</p>
                <Badge variant="secondary" size="sm">
                  {template.widgets.length} widgets
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Widget Picker Modal */}
      <Modal
        isOpen={showWidgetPicker}
        onClose={() => setShowWidgetPicker(false)}
        size="lg"
        header={{ title: 'Add Widget' }}
      >
        <ModalSection spacing="tight">
          <Search
            value={widgetSearch}
            onChange={setWidgetSearch}
            placeholder="Search widgets..."
            variant="filled"
            size="md"
          />
        </ModalSection>

        <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
          <Segment
            options={WIDGET_CATEGORY_OPTIONS}
            value={widgetCategory}
            onChange={setWidgetCategory}
            size="sm"
          />
        </div>

        <div className="widget-grid">
          {filteredWidgets.map(widget => (
            <div
              key={widget.id}
              className="widget-option"
              onClick={() => handleAddWidget(widget.id)}
            >
              <span className="widget-icon">{widget.icon}</span>
              <div className="widget-info">
                <h4>{widget.name}</h4>
                <p>{widget.description}</p>
              </div>
              <Badge variant="secondary" size="sm">
                {widget.default_width}x{widget.default_height}
              </Badge>
            </div>
          ))}
        </div>
      </Modal>

      {/* Actions Modal (replaces IonActionSheet) */}
      <Modal
        isOpen={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        size="sm"
        header={{
          title: actionDashboard?.name || 'Dashboard',
          subtitle: 'Choose an action',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 16 }}>
          <Button
            fill="outline"
            expand="block"
            iconStart={pencil}
            onClick={() => {
              if (actionDashboard) {
                onSelectDashboard?.(actionDashboard.id, true);
                setShowActionsModal(false);
              }
            }}
          >
            Edit Layout
          </Button>
          <Button
            fill="outline"
            expand="block"
            iconStart={apps}
            onClick={() => {
              if (actionDashboard) {
                setSelectedDashboardId(actionDashboard.id);
                setShowActionsModal(false);
                setShowWidgetPicker(true);
              }
            }}
          >
            Add Widget
          </Button>
          <Button
            fill="outline"
            expand="block"
            iconStart={colorPaletteOutline}
            onClick={() => {
              setShowActionsModal(false);
              setShowSettingsModal(true);
            }}
          >
            Background & Settings
          </Button>
          <Button
            fill="outline"
            expand="block"
            iconStart={checkmark}
            onClick={() => {
              if (actionDashboard) handleSetDefault(actionDashboard);
            }}
          >
            Set as Default
          </Button>
          <Button
            fill="outline"
            expand="block"
            iconStart={copy}
            onClick={() => {
              if (actionDashboard) handleDuplicate(actionDashboard);
            }}
          >
            Duplicate
          </Button>
          <Button
            variant="danger"
            expand="block"
            iconStart={trash}
            onClick={() => {
              setShowActionsModal(false);
              setShowDeleteModal(true);
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal (replaces IonAlert) */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="sm"
        header={{
          title: 'Delete Dashboard',
        }}
        footer={{
          primaryAction: {
            label: 'Delete',
            onClick: handleDelete,
            variant: 'danger',
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => setShowDeleteModal(false),
          },
        }}
      >
        <ModalSection spacing="normal">
          <p style={{
            fontFamily: 'var(--kff-data-font)',
            color: 'var(--ion-color-medium)',
            textAlign: 'center',
            margin: 0,
          }}>
            Are you sure you want to delete "<strong>{actionDashboard?.name}</strong>"?
            This cannot be undone.
          </p>
        </ModalSection>
      </Modal>

      {/* Dashboard Settings Modal */}
      <DashboardSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        dashboard={actionDashboard}
        householdId={householdId}
        onSave={(updatedDashboard) => {
          setDashboards(prev =>
            prev.map(d => d.id === updatedDashboard.id ? updatedDashboard : d)
          );
        }}
      />
    </PageShell>
  );
};

export default DashboardEditor;
